import { Icon } from '@iconify/react';
import { useMutation, useQuery } from '@tanstack/react-query';
/**
 * Her morning, his way.
 *
 * The old screen was six stacked buckets — Late, Right now, Today, Upcoming,
 * Waiting on me, Done today — each a heading over a list of identical cards.
 * Six lists is not a priority; a housekeeper opening her phone at 08:00 had
 * to read all of them to find out what she is doing at 08:00.
 *
 * His screen answers that with big cards, one decision each: a photo header,
 * the job in 18px, where and when, and the single pill that moves it. What is
 * done fades to a tick; what is still to come carries the soft grey pill.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ApiError, type Operation, type OperationTimer, api, isQueuedOffline } from '../api';
import { opPhoto } from '../assets/opPhotos';
import { Avatar, useAvatarUrl } from '../components/Avatar';
import { EnableNotifications } from '../components/EnableNotifications';
import { mapHref } from '../components/LocationLinks';
import { toastError, toastOk, toastQueued } from '../components/Toasts';
import { TranslateButton } from '../components/TranslateButton';
import { Sheet } from '../components/ui/Sheet';
import { SkeletonScreen } from '../components/ui/Skeleton';
import { getLang, pickTranslation, t } from '../i18n';
import { get, invalidate } from '../query';
import { ClockCard } from './ClockCard';
import { StaffTour } from './StaffTour';

// Subset of /v1/me used to decide whether the first-time tour should show.
// Combined shape covering both features that read /v1/me here:
// - the header avatar + greeting (avatars agent)
// - the first-visit tour gate (staff tour agent)
// We fetch /v1/me once and let both features derive from this single state.
interface MeRow {
  id: string;
  role: 'owner' | 'staff';
  full_name: string;
  avatar_r2_key: string | null;
  staff_tour_seen_at: string | null;
}

// The planned half of her day — what the owner said today looks like. The
// server already filters /v1/scheduled-shifts to her own rows for a staff
// token, so there is nothing to scope on the client.
interface PlannedShiftRow {
  id: string;
  starts_at: string;
  ends_at: string;
  on_call: boolean;
}

function timeOfDayGreeting(): string {
  const h = new Date().getHours();
  if (h < 12) return t('Good morning');
  if (h < 18) return t('Good afternoon');
  return t('Good evening');
}

type Bucket = 'late' | 'now' | 'today' | 'upcoming' | 'waiting_me' | 'done_today';

/**
 * The task timer is proof of effort, never part of the work itself — so its
 * calls are fire-and-forget. A timer call that fails must never block, delay
 * or error the transition it rode along with; the worst case is a missing
 * time record, which the next start's auto-stop quietly repairs.
 */
function timerCall(opId: string, action: 'start' | 'stop') {
  void api(`/v1/operations/${opId}/timer/${action}`, { method: 'POST', body: '{}' }).catch(
    () => {},
  );
}

/** "01:24:36" — whole seconds elapsed since the server-stamped started_at. */
function formatElapsed(startedAt: string): string {
  const secs = Math.max(0, Math.floor((Date.now() - Date.parse(startedAt)) / 1000));
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

/**
 * The quiet live timer on a running task card. Ticks every thirty seconds
 * off the SERVER's started_at — the client clock is only ever a renderer,
 * never a writer.
 */
function ElapsedTimer({ startedAt }: { startedAt: string }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className="flex items-center gap-1.5 text-[12px] font-bold tabular-nums text-primary">
      <Icon icon="ph:timer-bold" className="text-sm" />
      {formatElapsed(startedAt)}
    </span>
  );
}

export function StaffToday() {
  const [proofOp, setProofOp] = useState<Operation | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [tourSeen, setTourSeen] = useState(false);
  // The job she was just on, held for one beat before the next card in the
  // queue takes its place. Purely local UI state — the real completion
  // already happened (transition.mutate / the proof POST); this only
  // decides how long the first card shows a checkmark before showing whatever
  // is next. Holding the title here too, not just a boolean, so the
  // celebration reads "Watered the plants ✓" rather than a bare tick.
  const [justCompleted, setJustCompleted] = useState<string | null>(null);
  useEffect(() => {
    if (!justCompleted) return;
    const timer = setTimeout(() => setJustCompleted(null), 900);
    return () => clearTimeout(timer);
  }, [justCompleted]);
  const location = useLocation();
  const nav = useNavigate();
  // Banner shown once after StaffReceipt navigates back here. We swallow
  // the location.state on first render so a refresh doesn't re-trigger it.
  const [receiptSent, setReceiptSent] = useState(
    Boolean((location.state as { receiptProposalSent?: boolean } | null)?.receiptProposalSent),
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only on purpose. This effect exists to swallow the location.state that receiptSent was seeded from; listing receiptSent, nav or location.pathname would re-run it after the replace() it performs, and re-navigating on every state change would fight the router mid-render.
  useEffect(() => {
    if (receiptSent) {
      // Clear the nav state so a refresh doesn't re-show the banner.
      nav(location.pathname, { replace: true, state: null });
    }
  }, []);

  // Her day, kept warm. The thirty-second poll is the same one this screen
  // already ran; what is new is that the answer survives leaving the screen,
  // so coming back from a task does not blank the page and start again.
  //
  // No `tab` param means the server's "everything" view: every task that
  // isn't yet approved/cancelled comes back in full regardless of how old its
  // updated_at is, so a task scheduled for next week can never be silently
  // pushed out by a busy day of approvals elsewhere in the house. Only the
  // approved/cancelled history is paginated (next_cursor/has_more) — this
  // screen has never needed to page through history, so it isn't wired up
  // here; a screen that wants older completed tasks would pass `cursor`.
  const opsQ = useQuery({
    queryKey: ['/v1/operations'],
    queryFn: get<{ items: Operation[]; next_cursor: string | null; has_more: boolean }>,
    refetchInterval: 30_000,
  });
  const ops = opsQ.data?.items ?? [];

  // One /v1/me covers the header greeting, the avatar, and the first-visit
  // tour gate. Shared with every other screen that asks the same question.
  const meQ = useQuery({ queryKey: ['/v1/me'], queryFn: get<{ user: MeRow }> });
  const me = meQ.data?.user ?? null;

  // Her shift for today, if the owner planned one — one quiet line under the
  // date, never a card. Stable key within a day: start/end-of-day in local
  // time, hashed by value.
  const dayFrom = new Date();
  dayFrom.setHours(0, 0, 0, 0);
  const dayTo = new Date(dayFrom.getTime() + 86_400_000);
  const plannedQ = useQuery({
    queryKey: [
      `/v1/scheduled-shifts?from=${encodeURIComponent(dayFrom.toISOString())}&to=${encodeURIComponent(dayTo.toISOString())}`,
    ],
    queryFn: get<{ items: PlannedShiftRow[] }>,
  });
  const todayShift = plannedQ.data?.items[0] ?? null;
  // Same cached signed-URL lookup the header Avatar and every other avatar
  // in the app already use (GET /v1/uploads/avatar-url/:user_id under the
  // hood). Resolves to `undefined` while loading and `null` once we know
  // there is no photo — the welcome slide falls back to initials either way.
  const meAvatarUrl = useAvatarUrl(me?.id);

  const showTour = me?.role === 'staff' && me.staff_tour_seen_at === null && !tourSeen;

  const transition = useMutation({
    mutationFn: ({ opId, to }: { opId: string; to: string }) =>
      api(`/v1/operations/${opId}/transitions/${to}`, { method: 'POST', body: '{}' }),
    onSuccess: (_r, { to }) => {
      if (to === 'waiting_approval') toastOk(t('Sent to your owner'));
    },
    onError: (e) => (isQueuedOffline(e) ? toastQueued(e.message) : toastError(e)),
    onSettled: () => invalidate('/v1/operations'),
  });

  const buckets: Record<Bucket, Operation[]> = {
    late: [],
    now: [],
    today: [],
    upcoming: [],
    waiting_me: [],
    done_today: [],
  };
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);
  const endOfDay = new Date();
  endOfDay.setHours(23, 59, 59, 999);

  for (const op of ops) {
    // Late work goes first and stays first. The server decides this — until
    // now nothing on this screen distinguished a task whose deadline passed
    // yesterday from one due next week, so they sat in the same list and the
    // late one lost.
    if (op.state === 'overdue' || op.state === 'escalated') {
      buckets.late.push(op);
    } else if (op.state === 'in_progress' || op.state === 'waiting_proof') {
      buckets.now.push(op);
    } else if (op.state === 'sent' || op.state === 'seen' || op.state === 'scheduled') {
      // Pending work splits between TODAY (scheduled-for-today or no
      // specific date) and UPCOMING (scheduled for a future day). The
      // upcoming bucket is grouped by date so the staff sees "tomorrow",
      // "Sat 23/5", etc. instead of one long list.
      const when = op.scheduledFor ?? op.dueAt ?? null;
      if (!when) {
        buckets.today.push(op);
      } else {
        const whenMs = new Date(when).getTime();
        if (whenMs <= endOfDay.getTime()) buckets.today.push(op);
        else buckets.upcoming.push(op);
      }
    } else if (op.state === 'rejected') {
      buckets.waiting_me.push(op);
    } else if (
      (op.state === 'approved' || op.state === 'waiting_approval') &&
      new Date(op.updatedAt ?? op.createdAt).getTime() >= startOfDay.getTime()
    ) {
      buckets.done_today.push(op);
    }
  }
  // Chronological inside every bucket. "Coming next" that is not in the order
  // it is coming in is a list, and she has to read all of it — which is the
  // thing this screen exists to stop.
  const byTime = (a: Operation, b: Operation) =>
    new Date(a.scheduledFor ?? a.dueAt ?? a.createdAt).getTime() -
    new Date(b.scheduledFor ?? b.dueAt ?? b.createdAt).getTime();
  buckets.late.sort(byTime);
  buckets.now.sort(byTime);
  buckets.today.sort(byTime);
  buckets.upcoming.sort(byTime);

  // Late beats running beats waiting — the same order the six buckets were
  // in, collapsed into one stack of cards. The first card carries the orange
  // pill; everything under it carries the soft grey one.
  const queue = [...buckets.late, ...buckets.now, ...buckets.today];
  const hero = queue[0] ?? null;

  const doneCount = buckets.done_today.length;
  const totalToday = queue.length + doneCount;

  // His announcement band. There is no broadcast in this product — but a task
  // that belongs to an evening carries that evening on the row, and "guests at
  // 19:30" is exactly what it is. Real, denormalised, already on the wire.
  const eventOp = queue.find((o) => o.event_title) ?? null;

  const today = new Date().toLocaleDateString(localeFor(getLang()), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  if (opsQ.isPending) return <SkeletonScreen rows={3} />;

  // A failed fetch is not "you have no work today" — it is "we don't know".
  // Falling through to the empty-state buckets below used to render the
  // exact same screen for both, which is the one failure mode this product
  // cannot afford: a housekeeper who never learns her tasks didn't load has
  // no reason to think anything is wrong. Same pattern as OperationDetail /
  // Profile — a plain card with the server's own message and a retry.
  if (opsQ.isError) {
    return (
      <div className="mx-auto w-full max-w-lg pt-10">
        <div className="rounded-[24px] border border-urgent/25 bg-urgent/10 p-8 text-center">
          <Icon icon="ph:wifi-slash-bold" className="mx-auto mb-3 text-3xl text-urgent" />
          <p className="text-sm font-bold text-urgent">
            {opsQ.error instanceof ApiError ? opsQ.error.message : t("Couldn't load your tasks.")}
          </p>
          <button
            type="button"
            onClick={() => void opsQ.refetch()}
            className="mt-4 rounded-2xl bg-urgent/10 px-6 py-3.5 text-xs font-bold uppercase tracking-widest text-urgent transition-colors active:bg-urgent/20"
          >
            {t('Tap to retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 pb-40">
      {/* His header. The layout's own bar is desktop-only now — on a phone
          his screens carry their heading, and two headings is one too many. */}
      <header className="flex items-start justify-between gap-4 px-1 pt-8">
        <div className="min-w-0">
          <h1 className="font-heading text-[32px] font-semibold leading-none tracking-tight">
            {me ? (
              <>
                {timeOfDayGreeting()},
                <br />
                {me.full_name.split(' ')[0]}
              </>
            ) : (
              timeOfDayGreeting()
            )}
          </h1>
          <p className="mt-2 text-[13px] font-medium tracking-tight text-muted-foreground">
            {today}
          </p>
          {todayShift && (
            <p className="mt-1 text-[12px] font-semibold tracking-tight text-muted-foreground">
              {t('Shift')}{' '}
              {new Date(todayShift.starts_at).toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
              })}
              –
              {new Date(todayShift.ends_at).toLocaleTimeString(undefined, {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </p>
          )}
        </div>
        {me && (
          <Link
            to="/profile"
            aria-label={t('Profile')}
            className="relative shrink-0 overflow-visible rounded-[22px] border border-border bg-card p-1 shadow-[0_4px_12px_rgba(0,0,0,0.04)]"
          >
            <Avatar name={me.full_name} userId={me.id} size="md" shape="square" />
            <span className="absolute -bottom-1 -end-1 h-4 w-4 rounded-full border-2 border-card bg-wealth" />
          </Link>
        )}
      </header>

      {/* One tap when she arrives, one when she leaves. */}
      <ClockCard />

      <EnableNotifications />

      {receiptSent && (
        <div className="rounded-[24px] border border-wealth/25 bg-wealth/10 p-5">
          <div className="text-sm font-bold text-wealth">{t('Sent to your owner')}</div>
          <div className="mt-1 text-xs font-medium text-muted-foreground">
            {t('They will see the proposal and approve or reject it.')}
          </div>
          <button
            type="button"
            className="mt-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground underline"
            onClick={() => setReceiptSent(false)}
          >
            {t('Dismiss')}
          </button>
        </div>
      )}

      {eventOp?.event_title && (
        <section>
          <div className="flex items-center gap-4 rounded-[24px] border border-task/20 bg-task/10 p-5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-task/15 text-task">
              <Icon icon="ph:megaphone-fill" className="text-xl" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold leading-snug text-task" dir="auto">
                {eventOp.event_title}
              </p>
              {eventOp.event_starts_at && (
                <p className="mt-0.5 text-[10px] font-bold uppercase tracking-widest text-task/70">
                  {new Date(eventOp.event_starts_at).toLocaleTimeString(undefined, {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      <section className="space-y-5">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            {t('Today')}
          </h2>
          <span className="text-[11px] font-bold text-primary">
            {totalToday === 1 ? t('1 task') : t('{{n}} tasks').replace('{{n}}', String(totalToday))}
          </span>
        </div>

        {justCompleted && <HeroDoneCard title={justCompleted} />}

        <div className="flex flex-col gap-5">
          {queue.length === 0 && !justCompleted ? (
            <div className="rounded-[24px] border border-border bg-card p-8 text-center">
              <p className="text-sm font-bold text-muted-foreground">
                {t('Nothing to do right now.')}
              </p>
            </div>
          ) : (
            queue.map((op, i) => (
              <TaskCard
                key={op.id}
                op={op}
                primary={i === 0 && !justCompleted}
                busy={transition.isPending}
                onStart={() => {
                  // Starting the work also starts its timer — the timer never
                  // blocks the transition (see timerCall).
                  timerCall(op.id, 'start');
                  transition.mutate({ opId: op.id, to: 'in_progress' });
                }}
                onProof={() => setProofOp(op)}
                onDone={() => {
                  timerCall(op.id, 'stop');
                  setJustCompleted(pickTranslation(op.titleTranslations, op.title));
                  transition.mutate({ opId: op.id, to: 'waiting_approval' });
                }}
              />
            ))
          )}

          {/* What is done today fades to a tick — proof the day is moving,
              not another decision to read. */}
          {buckets.done_today.map((op) => (
            <DoneCard key={op.id} op={op} />
          ))}
        </div>
      </section>

      {buckets.waiting_me.length > 0 && (
        <section className="space-y-4">
          <h2 className="px-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
            {t('Waiting on me')}
          </h2>
          <div className="space-y-3">
            {buckets.waiting_me.map((op) => (
              <OpRow key={op.id} op={op} tone="urgent" />
            ))}
          </div>
        </section>
      )}

      {buckets.upcoming.length > 0 && <UpcomingByDay ops={buckets.upcoming} />}

      <section className="grid grid-cols-2 gap-3">
        <Link
          to="/today/receipt"
          className="flex flex-col items-center gap-2 rounded-[24px] border border-border bg-card p-5 transition-colors active:bg-secondary"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-primary/5">
            <Icon icon="ph:receipt-bold" className="text-xl text-primary" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest">
            {t('Scan a receipt')}
          </span>
        </Link>
        <button
          type="button"
          onClick={() => setIssueOpen(true)}
          className="flex flex-col items-center gap-2 rounded-[24px] border border-border bg-card p-5 transition-colors active:bg-secondary"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-[14px] bg-urgent/5">
            <Icon icon="ph:warning-circle-bold" className="text-xl text-urgent" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest">
            {t('Report something')}
          </span>
        </button>
      </section>

      {proofOp && (
        <ProofSheet
          op={proofOp}
          onClose={() => setProofOp(null)}
          onDone={() => {
            // Proof sent is the work finished — stop its timer too.
            timerCall(proofOp.id, 'stop');
            setJustCompleted(pickTranslation(proofOp.titleTranslations, proofOp.title));
            setProofOp(null);
            invalidate('/v1/operations');
          }}
        />
      )}

      {issueOpen && (
        <IssueSheet
          ops={queue}
          onClose={() => setIssueOpen(false)}
          onSent={() => {
            setIssueOpen(false);
            toastOk(t('Sent to your owner'));
            invalidate('/v1/operations');
          }}
        />
      )}

      {showTour && me && (
        <StaffTour
          fullName={me.full_name}
          // undefined (still resolving) collapses to initials same as null
          // (no photo set) — SlideWelcome only special-cases a truthy URL.
          avatarUrl={meAvatarUrl ?? null}
          onClose={() => {
            // Local flip so the overlay disappears immediately. /v1/me will
            // also reflect staff_tour_seen_at on the next mount, so even if
            // this state were lost the server would prevent re-show.
            setTourSeen(true);
            invalidate('/v1/me');
          }}
        />
      )}
    </div>
  );
}

function localeFor(lang: string): string {
  return lang === 'he' ? 'he-IL' : lang === 'ur' ? 'ur-PK' : lang === 'tl' ? 'fil-PH' : 'en-US';
}

/**
 * Sits where the first card was, for one beat, the moment a task is marked
 * done. This was previously nothing — the card was simply gone on the next
 * render, on the single most frequent action in the app. `justCompleted` in
 * the parent already unmounts this in favor of the real next card (or the
 * "nothing to do" state) on its own timer; this component only has to look
 * good for that one beat, including under prefers-reduced-motion, where the
 * global override in styles.css collapses both animations to near-instant.
 */
function HeroDoneCard({ title }: { title: string }) {
  return (
    <div
      className="rounded-[24px] border border-wealth/25 bg-wealth/10 p-8 text-center"
      style={{ animation: 'fm-hero-done 200ms ease-out' }}
    >
      <span
        className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-wealth text-white"
        style={{ animation: 'fm-check-pop 400ms cubic-bezier(0.34, 1.56, 0.64, 1)' }}
      >
        <Icon icon="ph:check-bold" className="text-3xl" />
      </span>
      <p className="font-heading text-lg font-bold text-wealth">{t('Nice work')}</p>
      <p className="mt-1 truncate text-sm font-medium text-wealth/80" dir="auto">
        {title}
      </p>
    </div>
  );
}

type ProofKind = 'photo' | 'voice' | 'text';
const PROOF_TYPE_LABEL: Record<ProofKind, string> = {
  photo: 'Photo',
  voice: 'Voice',
  text: 'Text',
};

/**
 * Which kinds of proof registerProof (apps/api/src/operations/service.ts)
 * will actually accept for this task. Empty/undefined means the owner never
 * restricted it — the server treats that the same as "any kind", so the
 * client falls back the same way rather than showing a requirement that
 * isn't real.
 */
function allowedProofTypes(op: Pick<Operation, 'proofTypes'>): ProofKind[] {
  return op.proofTypes && op.proofTypes.length > 0 ? op.proofTypes : ['photo', 'voice', 'text'];
}

function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * One job, at the size of the decision.
 *
 * A photo header, the title in 18px, where and when in one grey line, and
 * exactly one pill under it. Which pill depends on where the task is: work
 * that has not begun starts, work in flight either sends its photograph or
 * is handed over for approval. The first card in the queue gets the orange
 * pill — the one she should press now; the rest get the soft grey one.
 */
function TaskCard({
  op,
  primary,
  busy,
  onStart,
  onProof,
  onDone,
}: {
  op: Operation;
  primary: boolean;
  busy: boolean;
  onStart: () => void;
  onProof: () => void;
  onDone: () => void;
}) {
  const running = op.state === 'in_progress' || op.state === 'waiting_proof';
  const late = op.state === 'overdue' || op.state === 'escalated';
  const when = op.scheduledFor ?? op.dueAt ?? null;
  const title = pickTranslation(op.titleTranslations, op.title);

  // The timer on this task, if one is running. Fetched only for cards in a
  // working state — at most a couple on any given day. The server stamps
  // started_at; this screen only renders against it.
  const timerQ = useQuery({
    queryKey: [`/v1/operations/${op.id}/timer`],
    queryFn: get<OperationTimer>,
    enabled: running,
    refetchInterval: 30_000,
  });
  const session = timerQ.data?.running ?? null;

  // The one deliberate timer tap on this screen. Errors stay quiet — a stop
  // that never landed is repaired by the next start's auto-stop, and the
  // work itself (the transition) is unaffected either way.
  async function stopTimer() {
    try {
      await api(`/v1/operations/${op.id}/timer/stop`, { method: 'POST', body: '{}' });
    } catch {
      // quiet on purpose — see above
    }
    invalidate(`/v1/operations/${op.id}/timer`);
  }

  const action = !running
    ? // A card further down the queue with a clock on it reads as its hour —
      // "Start 15:30" — the way his design says "not yet, but here it is".
      when && !primary && !late
      ? { label: t('Start at {{time}}').replace('{{time}}', timeLabel(when)), run: onStart }
      : { label: t('Start work'), run: onStart }
    : op.proofRequired
      ? { label: t('Send proof'), run: onProof }
      : { label: t('Mark done'), run: onDone };

  const chip = late
    ? t('Late')
    : op.priority !== 'normal'
      ? t(op.priority === 'emergency' ? 'Emergency' : 'Important')
      : null;

  return (
    <div className="overflow-hidden rounded-[24px] border border-border bg-card shadow-[0_8px_30px_rgba(0,0,0,0.02)] transition-transform active:scale-[0.99]">
      <Link to={`/op/${op.id}`} className="relative block h-44 w-full" aria-label={t('Open')}>
        <img src={opPhoto(title)} alt="" className="h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        {chip && (
          <span className="absolute end-4 top-4 rounded-full border border-white/20 bg-white/90 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-foreground shadow-sm backdrop-blur-md">
            {chip}
          </span>
        )}
      </Link>
      <div className="flex flex-col gap-4 p-6">
        <div className="flex flex-col gap-1">
          <h3 className="font-heading text-[18px] font-semibold leading-tight" dir="auto">
            {title}
          </h3>
          <TranslateButton text={op.title} />
          <p className="text-[13px] font-medium tracking-tight text-muted-foreground">
            {op.locationText ? (
              <a
                href={mapHref(op.locationText)}
                target="_blank"
                rel="noreferrer"
                className="underline-offset-2 hover:underline"
                dir="auto"
              >
                {op.locationText}
              </a>
            ) : null}
            {op.locationText && when ? ' · ' : ''}
            {when ? timeLabel(when) : !op.locationText ? t('No time set') : null}
          </p>
          {op.proofRequired && (
            <p className="mt-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-primary">
              <Icon icon="ph:seal-check-bold" className="text-sm" />
              {t('Proof required')} ·{' '}
              {allowedProofTypes(op)
                .map((ty) => t(PROOF_TYPE_LABEL[ty]))
                .join(' · ')}
            </p>
          )}
          {op.checklist && op.checklist.length > 0 && (
            <p className="mt-1 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              <Icon icon="ph:list-checks-bold" className="text-sm" />
              {t('{{done}} of {{total}} steps done')
                .replace('{{done}}', String(op.checklist.filter((c) => c.done).length))
                .replace('{{total}}', String(op.checklist.length))}
            </p>
          )}
        </div>
        {session && (
          <div className="flex items-center justify-between rounded-[16px] border border-primary/15 bg-primary/5 px-4 py-2.5">
            <ElapsedTimer startedAt={session.started_at} />
            <span className="text-[10px] font-bold uppercase tracking-widest text-primary/70">
              {t('Timer running')}
            </span>
          </div>
        )}
        <div className={session ? 'flex gap-2' : undefined}>
          <button
            type="button"
            onClick={action.run}
            disabled={busy}
            className={`h-14 rounded-full text-[15px] font-bold tracking-wide transition-all active:scale-[0.98] disabled:opacity-50 ${
              session ? 'flex-1' : 'w-full'
            } ${
              primary
                ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20'
                : 'bg-secondary text-muted-foreground'
            }`}
          >
            {action.label}
          </button>
          {session && (
            <button
              type="button"
              onClick={() => void stopTimer()}
              className="h-14 shrink-0 rounded-full bg-secondary px-5 text-[13px] font-bold text-muted-foreground transition-all active:scale-[0.98]"
            >
              {t('Stop')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Done today: a faded thumbnail, a struck-through title, and the green tick. */
function DoneCard({ op }: { op: Operation }) {
  const title = pickTranslation(op.titleTranslations, op.title);
  return (
    <Link
      to={`/op/${op.id}`}
      className="flex items-center gap-4 rounded-[24px] border border-border bg-card/50 p-5 opacity-60 transition-transform active:scale-[0.99]"
    >
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-[18px] border border-black/[0.03]">
        <img src={opPhoto(title)} alt="" className="h-full w-full object-cover grayscale" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 text-start">
        <h3
          className="truncate text-[15px] font-bold line-through decoration-muted-foreground/30"
          dir="auto"
        >
          {title}
        </h3>
        <div className="flex items-center gap-1.5">
          <Icon icon="solar:check-circle-bold" className="text-sm text-wealth" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-wealth">
            {t('Done')}
          </span>
        </div>
      </div>
    </Link>
  );
}

/** Everything after the big cards: a row, an hour, and a way in. */
function OpRow({ op, tone }: { op: Operation; tone?: 'urgent' }) {
  const when = op.scheduledFor ?? op.dueAt ?? null;
  return (
    <Link
      to={`/op/${op.id}`}
      className={`flex w-full items-center justify-between gap-3 rounded-[24px] border border-border bg-card p-5 transition-transform active:scale-[0.99] ${
        tone === 'urgent' ? 'border-s-4 border-s-urgent' : ''
      }`}
    >
      <div className="flex min-w-0 items-center gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[14px] bg-secondary text-muted-foreground">
          <Icon icon={op.proofRequired ? 'ph:camera-bold' : 'ph:checks-bold'} className="text-xl" />
        </div>
        <div className="min-w-0 text-start">
          <h4 className="truncate text-[15px] font-bold" dir="auto">
            {pickTranslation(op.titleTranslations, op.title)}
          </h4>
          <p className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {when ? t('Start at {{time}}').replace('{{time}}', timeLabel(when)) : t('No time set')}
          </p>
        </div>
      </div>
      <Icon
        icon="solar:alt-arrow-right-bold"
        className="shrink-0 text-xl text-[#C4C4C2] rtl:rotate-180"
      />
    </Link>
  );
}

// Not a real operation id — the "this isn't about any task" choice in the
// picker below. Kept out of the id namespace on purpose so it can never
// collide with a real op_… id.
const GENERAL_REPORT = '__general__';

/**
 * Something is wrong, and it may or may not be about a job.
 *
 * The `issue` update kind is a thing this product has always accepted, and
 * until now the only way to write one was to open a task and find the right
 * chip — which meant a washing machine that leaks, with no task attached to
 * it, had nowhere to go but WhatsApp. `GENERAL_REPORT` posts to
 * /v1/staff-messages instead — a standalone note to every owner, still
 * inside the app.
 */
function IssueSheet({
  ops,
  onClose,
  onSent,
}: {
  ops: Operation[];
  onClose: () => void;
  onSent: () => void;
}) {
  const [opId, setOpId] = useState(ops[0]?.id ?? GENERAL_REPORT);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function send() {
    if (!body.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      // idempotency_key: sent once per submit, resent verbatim on an
      // offline-queue replay — closes the duplicate-report gap the comment
      // below used to only paper over at the UI layer.
      const idempotency_key = crypto.randomUUID();
      if (opId === GENERAL_REPORT) {
        await api('/v1/staff-messages', {
          method: 'POST',
          body: JSON.stringify({ body: body.trim(), source_lang: getLang(), idempotency_key }),
        });
      } else {
        await api(`/v1/operations/${opId}/updates`, {
          method: 'POST',
          body: JSON.stringify({
            kind: 'issue',
            body: body.trim(),
            source_lang: getLang(),
            idempotency_key,
          }),
        });
      }
      onSent();
    } catch (e) {
      // Queued for offline replay is not a failure — the report was
      // accepted. Leaving the sheet open with a red "failed" message here
      // invited a second, duplicate report of the same issue.
      if (isQueuedOffline(e)) {
        toastQueued(e.message);
        onSent();
        return;
      }
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet title={t('Report something')} onClose={onClose}>
      <select
        className="input"
        value={opId}
        onChange={(e) => setOpId(e.target.value)}
        aria-label={t('Which task is it about?')}
      >
        <option value={GENERAL_REPORT}>{t('Not about a specific task')}</option>
        {ops.map((o) => (
          <option key={o.id} value={o.id}>
            {pickTranslation(o.titleTranslations, o.title)}
          </option>
        ))}
      </select>
      <textarea
        className="input min-h-[100px]"
        placeholder={t('What is going on?')}
        aria-label={t('What is going on?')}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        dir="auto"
      />
      {err && <div className="text-sm text-urgent">{err}</div>}
      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={busy || !body.trim()}
          onClick={send}
        >
          {busy ? '…' : t('Send')}
        </button>
        <button type="button" className="btn-ghost" onClick={onClose}>
          {t('Cancel')}
        </button>
      </div>
    </Sheet>
  );
}

function UpcomingByDay({ ops }: { ops: Operation[] }) {
  // Group by the local-day string the staff will read on their phone.
  // "Tomorrow", "Sat 23/5", "Mon 25/5" — the relative form is friendlier
  // than raw ISO dates for the next ~7 days.
  const lang = getLang();
  const locale =
    lang === 'he' ? 'he-IL' : lang === 'ur' ? 'ur-PK' : lang === 'tl' ? 'fil-PH' : 'en-US';
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  const dayAfter = new Date(tomorrow);
  dayAfter.setDate(dayAfter.getDate() + 1);

  function dayLabel(d: Date): string {
    if (d.getTime() >= tomorrow.getTime() && d.getTime() < dayAfter.getTime()) {
      return t('Tomorrow');
    }
    return d.toLocaleDateString(locale, { weekday: 'short', day: '2-digit', month: '2-digit' });
  }

  const groups = new Map<string, { label: string; ops: Operation[] }>();
  for (const op of ops) {
    const when = new Date(op.scheduledFor ?? op.dueAt ?? op.createdAt);
    when.setHours(0, 0, 0, 0);
    const key = when.toISOString().slice(0, 10);
    if (!groups.has(key)) groups.set(key, { label: dayLabel(when), ops: [] });
    groups.get(key)!.ops.push(op);
  }

  return (
    <section>
      <h2 className="section-title">{t('Upcoming')}</h2>
      <div className="space-y-4">
        {Array.from(groups.entries()).map(([key, group]) => (
          <div key={key} className="space-y-2">
            <div className="px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {group.label}
            </div>
            {group.ops.map((op) => (
              <OpRow key={op.id} op={op} />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

/** The one place proof gets uploaded. The task page shows the same sheet —
 *  only the assignee may send proof, so there is one flow, not two. */
export function ProofSheet({
  op,
  onClose,
  onDone,
}: {
  op: Operation;
  onClose: () => void;
  onDone: () => void;
}) {
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [voice, setVoice] = useState<Blob | null>(null);
  const [recording, setRecording] = useState<MediaRecorder | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Which inputs this task will actually accept — see allowedProofTypes().
  // Showing only these closes the dead end where a staff member sent a photo
  // for a voice-only task, was rejected by the server, and had nothing on
  // screen telling her why or what to do instead.
  const allowed = allowedProofTypes(op);
  const canPhoto = allowed.includes('photo');
  const canVoice = allowed.includes('voice');
  const canText = allowed.includes('text');
  const hasValidProof = (canPhoto && file) || (canVoice && voice) || (canText && text.trim());

  async function record() {
    if (recording) {
      recording.stop();
      setRecording(null);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      const chunks: BlobPart[] = [];
      rec.ondataavailable = (e) => chunks.push(e.data);
      rec.onstop = () => {
        setVoice(new Blob(chunks, { type: 'audio/webm' }));
        for (const tk of stream.getTracks()) tk.stop();
      };
      rec.start();
      setRecording(rec);
    } catch {
      // getUserMedia failures are raw browser errors ('NotAllowedError:
      // Permission denied') — nothing she can act on worded like that.
      setErr(t('Microphone unavailable — type instead'));
    }
  }

  async function uploadToR2(
    blob: Blob | File,
    kind: 'proof' | 'voice',
    contentType: string,
  ): Promise<string> {
    const sign = await api<{
      put_url: string;
      r2_key: string;
      required_headers: Record<string, string>;
    }>('/v1/uploads/sign', {
      method: 'POST',
      body: JSON.stringify({
        kind,
        content_type: contentType,
        byte_length: blob.size,
      }),
    });
    const put = await fetch(sign.put_url, {
      method: 'PUT',
      headers: sign.required_headers,
      body: blob,
    });
    if (!put.ok) throw new Error(`R2 upload ${put.status}`);
    return sign.r2_key;
  }

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      let r2_key: string | undefined;
      let proofType: ProofKind = 'text';
      if (canPhoto && file) {
        r2_key = await uploadToR2(file, 'proof', file.type || 'image/jpeg');
        proofType = 'photo';
      } else if (canVoice && voice) {
        r2_key = await uploadToR2(voice, 'voice', 'audio/webm');
        proofType = 'voice';
      }
      await api(`/v1/operations/${op.id}/proofs`, {
        method: 'POST',
        body: JSON.stringify({
          type: proofType,
          r2_key,
          text_body: text || undefined,
        }),
      });
      onDone();
    } catch (e) {
      // The R2 PUT failure is a plain Error ('R2 upload 403'), not an
      // ApiError — falling back to its message would show her that string.
      setErr(e instanceof ApiError ? e.message : t('Failed'));
    } finally {
      setBusy(false);
    }
  }

  const textIsThePrimaryProof = canText && !canPhoto && !canVoice;

  return (
    <Sheet title={t('Send proof')} onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        {pickTranslation(op.titleTranslations, op.title)}
      </p>

      {op.proofRequired && (
        <p className="text-xs font-bold uppercase tracking-widest text-primary">
          {t('Proof required')} · {allowed.map((ty) => t(PROOF_TYPE_LABEL[ty])).join(' · ')}
        </p>
      )}

      {canPhoto &&
        (file ? (
          <div className="flex items-center justify-between gap-3 rounded-xl bg-secondary px-4 py-3 text-sm">
            <span className="min-w-0 truncate">{file.name}</span>
            <button
              type="button"
              onClick={() => setFile(null)}
              className="shrink-0 text-xs font-bold uppercase tracking-widest text-urgent"
            >
              {t('Remove')}
            </button>
          </div>
        ) : (
          <label className="block">
            <span className="text-sm text-muted-foreground">{t('Photo (camera)')}</span>
            <input
              className="mt-1 block w-full text-sm"
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
        ))}

      {canVoice && (
        <div>
          <button
            type="button"
            onClick={record}
            className={`w-full text-sm rounded-xl px-4 py-3 ${
              recording
                ? 'bg-danger/10 text-danger'
                : voice
                  ? 'bg-success/10 text-success'
                  : 'bg-secondary text-foreground/80'
            }`}
          >
            {recording
              ? t('Stop recording')
              : voice
                ? t('Voice ready · tap to re-record')
                : t('Voice note')}
          </button>
        </div>
      )}

      <textarea
        className="input min-h-[80px]"
        placeholder={
          textIsThePrimaryProof ? t('Describe what was done') : t('Short note (optional)')
        }
        value={text}
        onChange={(e) => setText(e.target.value)}
      />

      {err && <div className="text-danger text-sm">{err}</div>}

      <div className="flex gap-2">
        <button
          type="button"
          className="btn-primary flex-1"
          disabled={busy || !hasValidProof}
          onClick={submit}
        >
          {busy ? '…' : t('Send')}
        </button>
        <button type="button" className="btn-ghost" onClick={onClose}>
          {t('Cancel')}
        </button>
      </div>
    </Sheet>
  );
}
